import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, takeUntil, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AuthService } from '../../../core/common/services/auth/auth.service';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { ProfileDropdownComponent } from '../../../core/common/components/profile-dropdown/profile-dropdown.component';
import { NavbarBannerLogoComponent } from '../../../core/common/components/navbar-banner-logo/navbar-banner-logo.component';
import { appUrlChanges } from '../../../core/common/helpers/portal/app-url.helper';
import {
  resolveAppUrl,
  shouldSuppressNotifications,
} from '../../../core/common/helpers/portal/is-vendor-portal-url';
import { UrlListenerHelper } from '../../../core/common/helpers/url-listener/url-listener.helper';
import { LangSwitcherComponent } from '../../../core/common/components/lang-switcher/lang-switcher.component';

@Component({
  selector: 'lib-navbar',
  standalone: true,
  imports: [
    RouterModule,
    LangSwitcherComponent,
    NotificationBellComponent,
    ProfileDropdownComponent,
    NavbarBannerLogoComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NavbarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  readonly urlListenerHelper = inject(UrlListenerHelper);

  projectName = input<string>('project_name');
  navClass = input<string>('');
  showNotification = input.required<boolean>();
  hasNotification = input<boolean>(false);

  isLoggedIn = computed<boolean>(
    () =>
      !!this.authService.user() ||
      (!!this.authService.centralUser() &&
        !!this.authService.selectedWorkspace())
  );
  destroy$ = new Subject<void>();
  currentLocation = signal<string>('');
  isCentral = signal<boolean>(this.router.url.includes('central'));
  selectedWorkspace = computed(() => this.authService.selectedWorkspace());
  private readonly currentUrl = toSignal(appUrlChanges(this.router), {
    initialValue: resolveAppUrl(this.router.url),
  });
  protected readonly canUseNotifications = computed(
    () =>
      this.showNotification() &&
      !shouldSuppressNotifications(this.currentUrl() ?? '')
  );

  ngOnInit(): void {
    this.setCentral();
    this.setCurrentLocation();
    this.urlListenerHelper.listenToUrlChanges();
  }

  setCurrentLocation(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd),
        map(event => {
          const url = event.urlAfterRedirects.split('/')[2];
          if (url === 'admin') {
            return 'admin';
          }
          return 'employee';
        })
      )
      .subscribe((value: string) => {
        this.currentLocation.set(value);
      });
  }

  setCentral(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd),
        map(event => {
          const url = event.urlAfterRedirects;
          return url.includes('central');
        })
      )
      .subscribe((value: boolean) => {
        this.isCentral.set(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
